import { MouseEvent, useState } from 'react';
import { Column, ColumnDef } from '@tanstack/react-table';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import {
  ArrowUpDown,
  MessageSquareText,
  Paperclip,
  Trash2,
} from 'lucide-react';

import HelpDeskTicketTable from './TicketTable';
import { useDeleteTicket, useGetAllHelpDesk } from 'services/help-desk/hooks';
import Loader from 'components/Loader';
import useAuthStore from 'store/auth';
import { cn, getDateTime } from 'lib/utils';
import DeleteTicketDialog from '../../components/dialogs/DeleteTicketDialog';
import { Button } from 'components/ui/button';
import { HelpDeskTicket } from 'services/help-desk/type';

export default function HelpDeskTicketPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const profileId = useAuthStore((store) => store.currentUserProfileId);
  const [openDelete, setOpenDelete] = useState<boolean>(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data, isFetching } = useGetAllHelpDesk(profileId);

  const { mutate: deleteTickets, isPending: isDeleting } = useDeleteTicket();

  const handleDelete = () => {
    if (deleteId) {
      deleteTickets([deleteId], {
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ['help-desk-list'],
          });
          setOpenDelete(false);
          toast.success(t('ticket_deleted_msg'));
        },
        onError: () => {
          setOpenDelete(false);
        },
      });
    }
  };

  const columns: ColumnDef<HelpDeskTicket>[] = [
    {
      accessorKey: 'subject',
      header: ({ column }) => <SortButton column={column} header='Subject' />,
    },
    {
      accessorKey: 'created_at',
      header: ({ column }) => <SortButton column={column} header='Date' />,
      cell: ({ row }) => (
        <p className='text-center'>
          {getDateTime(row.getValue('created_at'), 'dd/MM/yyyy').date}
        </p>
      ),
    },
    {
      accessorKey: 'age',
      header: ({ column }) => (
        <SortButton column={column} header='Ticket Age' />
      ),
      cell: ({ row }) => (
        <p className='text-center'>{row.getValue('age')} days</p>
      ),
    },
    {
      accessorKey: 'priority',
      header: ({ column }) => <SortButton column={column} header='Priority' />,
      cell: ({ row }) => (
        <p className='text-center capitalize'>{row.getValue('priority')}</p>
      ),
    },
    {
      accessorKey: 'status',
      header: ({ column }) => <SortButton column={column} header='Status' />,
      cell: ({ row }) => (
        <div
          className={cn(
            'text-center capitalize w-fit mx-auto rounded-2xl px-3 py-1 text-xs border',
            row.getValue('status') === 'open'
              ? 'text-green-500 bg-green-100 border-green-300'
              : 'text-red-500 bg-red-100 border-red-300'
          )}
        >
          {row.getValue('status')}
        </div>
      ),
    },
    {
      accessorKey: 'actions',
      header: '',
      cell: ({ row }) => (
        <div className='flex items-center justify-between gap-4'>
          <div className='flex items-center justify-start gap-2 cursor-pointer'>
            <div className='space-x-4'>
              <p className='flex items-center justify-center gap-2 text-sm text-medsi-blue '>
                <MessageSquareText className='w-4 h-4' />
                <span>{row.getValue('ticket_comment') || 0}</span>
              </p>
            </div>
            <div className='space-x-4'>
              <p className='flex items-center justify-center gap-2 text-sm text-medsi-blue'>
                <Paperclip className='w-4 h-4' />
                <span>0</span>
              </p>
            </div>
          </div>
          <Button
            variant='ghost'
            onClick={(event: MouseEvent) => {
              event.stopPropagation();
              setDeleteId(row?.original?.ticket_id);
              setOpenDelete(true);
            }}
            data-testid={`delete-${row?.original?.ticket_id}`}
          >
            <Trash2 className='w-4 h-4 text-red-400 cursor-pointer ' />
          </Button>
        </div>
      ),
    },
  ];

  if (isFetching) {
    return <Loader />;
  }

  return (
    <>
      <HelpDeskTicketTable data={data || []} columns={columns} />

      <DeleteTicketDialog
        open={openDelete}
        onClose={() => setOpenDelete(false)}
        handleDelete={handleDelete}
        isDeleting={isDeleting}
      />
    </>
  );
}

function SortButton({
  column,
  header,
}: {
  column: Column<HelpDeskTicket>;
  header: string;
}) {
  return (
    <Button
      variant='ghost'
      className='h-fit hover:bg-transparent hover:text-medsi-blue'
      onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
    >
      <span>{header}</span>
      <ArrowUpDown className='w-4 h-4 ml-2' />
    </Button>
  );
}
